<script setup lang="ts">
import {onMounted, watch} from 'vue';
import {storeToRefs} from 'pinia';
import {useCrmAccountStore, useValueDataStore} from '@/stores';
import {notesActivitiesFields} from '@/crm/spec/notesActivitiesFields';
import type {EntityName, FieldDef, SectionDef} from '@/crm/spec/types';
import SimpleTable from '@/crm/components/SimpleTable.vue';
import NoteCard from '@/crm/components/NoteCard.vue';
import Spinner from '@/components/common/Spinner.vue';

const valueStore = useValueDataStore();
const crmStore = useCrmAccountStore();
const {selectionId} = storeToRefs(valueStore);
const {
    account, notes, activities,
    isAccountLoading, isNotesLoading, isActivitiesLoading,
    accountError,
} = storeToRefs(crmStore);

const activitiesSection: SectionDef = notesActivitiesFields.sections.find(s => s.entity === 'activitypointer')!;
const timelineSection: SectionDef = notesActivitiesFields.sections.find(s => s.entity === 'annotation')!;
const activitiesTitle: string = activitiesSection.name ?? '';
const activitiesEntity: EntityName = activitiesSection.entity;
const activitiesColumns: FieldDef[] = activitiesSection.columns ?? [];
const timelineTitle: string = timelineSection.name ?? '';

async function load() {
    await crmStore.ensureAccount();
    await Promise.all([crmStore.ensureNotes(), crmStore.ensureActivities()]);
}

onMounted(load);
watch(selectionId, load);
</script>

<template>
    <div class="crm-page">
        <div v-if="!selectionId" class="placeholder">Select a store or group to view its CRM record.</div>

        <template v-else>
            <div v-if="isAccountLoading && !account" class="loading"><Spinner/></div>
            <div v-else-if="accountError" class="error">Couldn't load account.</div>

            <template v-else-if="account">
                <div class="grid">
                    <SimpleTable
                        :title="activitiesTitle"
                        :entity="activitiesEntity"
                        :columns="activitiesColumns"
                        :rows="activities"
                    />
                    <section class="timeline">
                        <h2>{{ timelineTitle }}</h2>
                        <div v-if="!notes.length" class="empty">No notes</div>
                        <NoteCard v-for="(note, i) in notes" :key="i" :note="note"/>
                    </section>
                </div>
                <div v-if="isActivitiesLoading || isNotesLoading" class="loading-inline"><Spinner/></div>
            </template>
        </template>
    </div>
</template>

<style scoped>
.crm-page {
    padding: 1.5rem;
    max-width: 1400px;
    margin: 0 auto;
    color: var(--color-text);
}

.grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    align-items: start;
}

.timeline h2 {
    margin: 0 0 0.75rem;
    font-size: var(--font-size-h2, 1.1rem);
    font-weight: 600;
    color: var(--color-text);
}

.empty {
    opacity: 0.6;
    font-size: var(--font-size-body);
    color: var(--color-text);
    padding: 0.5rem 0;
}

@media (max-width: 1100px) {
    .grid {
        grid-template-columns: 1fr;
    }
}

.placeholder, .error {
    padding: 2rem;
    text-align: center;
    opacity: 0.7;
    font-size: var(--font-size-body);
}

.loading {
    display: flex;
    justify-content: center;
    padding: 3rem;
}

.loading-inline {
    display: flex;
    justify-content: center;
    padding: 0.5rem;
}
</style>
